import {Paragraph} from "@/pages/common/Home/Dissection/Article";

import article_5 from "@images/home/analytics/broader_sales_talent/article-5.png";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {H2} from "@ui/titles/H2";
import {H3} from "@ui/titles/H3";
import {useSelector} from "react-redux";

import styles from "../styles.module.scss";

export const ArticleBroader = () => {
  const {isMobile} = useSelector((state) => state.common.settings);

  return (
    <>
      <Paragraph>
        <H2>
          <span>
            Broader Sales Talent. Higher Expectations. A&nbsp;New&nbsp;Standard
            for&nbsp;Readiness
          </span>
        </H2>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Pharmaceutical innovation exists to&nbsp;improve patient outcomes.
              Yet&nbsp;between laboratory discovery and patient benefit lies
              a&nbsp;critical intermediary: conversation. The&nbsp;way clinical
              evidence is&nbsp;explained, challenged and contextualised
              in&nbsp;discussions with healthcare professionals (HCPs) plays
              a&nbsp;meaningful role in&nbsp;how new therapies are understood
              and adopted in&nbsp;practice. At&nbsp;the&nbsp;same time,
              the&nbsp;profile of&nbsp;those leading these conversations
              is&nbsp;changing.
            </span>
          </RootDesc>

          <article style={{marginTop: "26rem", marginBottom: "26rem"}}>
            <img
              src={article_5}
              alt={"image"}
            />
          </article>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>A Broader Talent Model</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Pharmaceutical commercial models across Europe have shifted
              towards hybrid engagement structures and greater
              use&nbsp;of&nbsp;Contract Sales Organisations&nbsp;(CSOs),
              increasing flexibility in&nbsp;how field teams are&nbsp;deployed
              <sup>1,2</sup>.
            </span>

            <span>
              Recruitment patterns have diversified in&nbsp;parallel.
              In&nbsp;the&nbsp;UK, representatives are not required to&nbsp;hold
              medical, pharmacy or&nbsp;nursing qualifications. Regulatory
              assurance is&nbsp;instead provided through completion
              of&nbsp;the&nbsp;ABPI Medical Representatives Examination within
              two years of&nbsp;appointment<sup>3</sup>.
            </span>

            <span>
              This structure prioritises commercial literacy and compliance
              certification rather than formal clinical training. As&nbsp;field
              teams become more diverse in&nbsp;academic background and
              deployment more flexible, ensuring consistent applied readiness
              becomes increasingly important.
            </span>

            <span>
              The commercial model has&nbsp;evolved structurally. Field teams
              are more diverse. Deployment is&nbsp;more flexible. Therapeutic
              environments are more complex. Yet&nbsp;governance mechanisms have
              not&nbsp;evolved at&nbsp;the&nbsp;same&nbsp;pace.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Certification, Cost, and&nbsp;the&nbsp;Training Burden</span>
        </H2>

        <div className={styles.article_text_image}>
          <div
            style={{
              paddingLeft: isMobile ? "0" : "120rem",
              width: isMobile ? "100%" : "690rem",
            }}
          >
            <RootDesc>
              <span>
                The ABPI examination establishes a&nbsp;regulatory baseline.
                It&nbsp;confirms knowledge of&nbsp;compliance frameworks and
                core scientific principles<sup>3</sup>.
              </span>

              <span>
                Certification, however, does&nbsp;not&nbsp;eliminate the need
                for&nbsp;structured onboarding and ongoing therapeutic
                education. For&nbsp;representatives entering the profession
                without formal clinical qualifications, training and rehearsal
                are operational necessities rather than discretionary
                investments.
              </span>

              <span>
                Pharmaceutical companies already commit significant resources
                to&nbsp;preparing their field organisations. Benchmark research
                across 25&nbsp;pharmaceutical companies conducted
                by&nbsp;Best&nbsp;Practices, LLC&nbsp;found that sales training
                functions operate with average annual budgets
                of&nbsp;approximately $5.9&nbsp;million, with more than
                one-third of&nbsp;that investment dedicated to&nbsp;onboarding
                new representatives<sup>8</sup>. This&nbsp;reflects
                the&nbsp;operational reality that field teams require structured
                education, rehearsal and&nbsp;ongoing scientific development
                before they can confidently engage healthcare professionals.
              </span>

              <span>
                Training is&nbsp;therefore embedded within the&nbsp;commercial
                model as&nbsp;an&nbsp;unavoidable cost. The&nbsp;question
                is&nbsp;not&nbsp;whether organisations train, but&nbsp;whether
                that investment translates efficiently into demonstrated
                readiness.
              </span>
            </RootDesc>
          </div>

          <div className={styles.article_text_card}>
            <div className={styles.article_broader_card}>
              <H3>
                <strong>$5.9</strong>
                <br />
                <span style={{color: "var(--violet)"}}>million</span>
              </H3>

              <br />

              <H3>
                <span style={{fontSize: "23rem"}}>
                  Average annual budgets for&nbsp;sales training functions
                </span>
              </H3>
            </div>
          </div>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>The&nbsp;Gap: Knowledge Versus Readiness</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Pharmaceutical organisations measure training completion and
              knowledge recall effectively. What&nbsp;remains harder
              to&nbsp;measure is&nbsp;applied readiness&nbsp;- the ability
              to&nbsp;perform credibly in&nbsp;a&nbsp;live, evidence-based
              discussion.
            </span>

            <span>
              Meta-analytic evidence supports the&nbsp;role of&nbsp;simulation
              and structured rehearsal in&nbsp;strengthening applied competence
              and communication performance in&nbsp;healthcare contexts
              <sup>6,7</sup>.
            </span>

            <div className={styles.article_broader_violet}>
              <RootDesc>
                <b>
                  Traditional training confirms understanding.
                  It&nbsp;does&nbsp;not&nbsp;consistently validate performance
                  under challenge. Field coaching provides insight, but
                  retrospectively.
                  <br /> <br />
                  The structural gap is&nbsp;pre-deployment validation.
                </b>
              </RootDesc>
            </div>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Beyond Launch: Continuous Knowledge Pressure</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Therapeutic landscapes evolve rapidly. Industry data show
              sustained shifts in&nbsp;innovation geography, R&D&nbsp;intensity
              and competitive dynamics across global markets<sup>8</sup>.
            </span>

            <span>
              Healthcare professionals operate in&nbsp;time-constrained and
              increasingly data-dense environments. Evidence from systematic
              reviews demonstrates that&nbsp;pharmaceutical company interactions
              can&nbsp;influence exposure to therapeutic information and
              prescribing patterns<sup>9</sup>.
            </span>

            <span>
              The clarity and integrity of&nbsp;scientific communication
              therefore matter. Development cannot be&nbsp;episodic;
              it&nbsp;must be&nbsp;continuous.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Why This Area Is&nbsp;Ripe for&nbsp;Automation</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Industry analysis highlights increasing complexity
              in&nbsp;customer engagement models and growing pressure
              on&nbsp;life sciences organisations to&nbsp;demonstrate measurable
              performance across commercial functions<sup>1,2</sup>.
            </span>

            <span>
              Where training is&nbsp;already embedded as&nbsp;a&nbsp;structural
              cost, automation offers optimisation rather than disruption.
              Simulation-enabled rehearsal tools can&nbsp;integrate alongside
              existing learning systems, functioning as&nbsp;a&nbsp;capability
              layer rather than&nbsp;replacing established governance
              frameworks.
            </span>

            <span>
              Extending structured rehearsal into regulated commercial
              environments is&nbsp;therefore not&nbsp;a&nbsp;paradigm shift,
              but&nbsp;an&nbsp;efficiency shift.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>From Knowledge to&nbsp;Demonstrated Readiness</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              AVA&nbsp;Trainer developed by&nbsp;Vivanti was&nbsp;designed
              to&nbsp;strengthen clinical conversations before they&nbsp;occur.
              Its&nbsp;significance lies not&nbsp;only
              in&nbsp;its&nbsp;technical capability, but
              in&nbsp;its&nbsp;structural role: AVA&nbsp;functions
              as&nbsp;a&nbsp;foundational layer beneath clinical dialogue.
            </span>

            <span>
              It does not&nbsp;replace certification;
              it&nbsp;operationalises&nbsp;it.
              It&nbsp;does&nbsp;not&nbsp;duplicate training content;
              it&nbsp;validates performance.
            </span>

            <span>
              Through scenario-based simulations, representatives rehearse
              clinical discussions, objection handling, compliance-sensitive
              messaging and stakeholder-specific engagement
              in&nbsp;a&nbsp;controlled environment. The&nbsp;focus
              is&nbsp;applied performance under challenge&nbsp;-
              the&nbsp;dimension that ultimately determines credibility
              in&nbsp;front of&nbsp;an&nbsp;HCP<sup>6</sup>.
            </span>

            <div className={styles.article_broader_violet}>
              <RootDesc>
                <b>
                  In&nbsp;hybrid commercial ecosystems, a&nbsp;shared readiness
                  tool introduces transparency across internal
                  and&nbsp;CSO&nbsp;teams. It&nbsp;reduces variability
                  and&nbsp;embeds verification before exposure.
                </b>
              </RootDesc>
            </div>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Looking Ahead</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Certification confirms baseline knowledge;
              it&nbsp;does&nbsp;not&nbsp;guarantee applied competence under
              scrutiny<sup>3</sup>.
            </span>

            <span>
              When readiness is&nbsp;assumed rather than validated, variability
              enters scientific dialogue. That&nbsp;variability influences
              interpretation, confidence and ultimately therapeutic integration
              <sup>9</sup>.
            </span>

            <span>
              Where trust in&nbsp;science must be&nbsp;actively maintained,
              erosion of&nbsp;credibility does&nbsp;not&nbsp;remain
              a&nbsp;commercial issue; it&nbsp;becomes a&nbsp;patient issue.
            </span>

            <span>
              Leaders who formalise applied readiness as&nbsp;a&nbsp;measurable
              capability (rather than&nbsp;an&nbsp;assumed outcome
              of&nbsp;training) safeguard scientific integrity
              at&nbsp;the&nbsp;point where innovation meets clinical practice.
            </span>

            <span>
              Delay carries risk. Incremental variability, conversation
              by&nbsp;conversation, gradually weakens credibility. Rebuilding
              trust is&nbsp;invariably more costly than verifying readiness
              in&nbsp;the&nbsp;first&nbsp;place.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_citations}>
          <h6>References</h6>

          <RootDesc>
            <ol>
              <li>
                <span>
                  Deloitte. (2025). 2025&nbsp;Life Sciences Executive Outlook.{" "}
                  <a
                    href={
                      "https://www.deloitte.com/us/en/insights/industry/health-care/life-sciences-and-health-care-industry-outlooks/2025-life-sciences-executive-outlook.html"
                    }
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://www.deloitte.com/us/en/insights/industry/health-care/life-sciences-and-health-care-industry-outlooks/2025-life-sciences-executive-outlook.html.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Deloitte. (2025). Unlocking the&nbsp;Future of&nbsp;Customer
                  Engagement in&nbsp;Pharma.{" "}
                  <a
                    href={
                      "https://www.deloitte.com/ch/en/Industries/life-sciences-health-care/analysis/unlocking-the-future-of-customer-engagement-in-pharma.html"
                    }
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://www.deloitte.com/ch/en/Industries/life-sciences-health-care/analysis/unlocking-the-future-of-customer-engagement-in-pharma.html.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Association of&nbsp;the&nbsp;British Pharmaceutical
                  Industry&nbsp;(ABPI). (2024). Code of&nbsp;Practice
                  for&nbsp;the&nbsp;Pharmaceutical Industry&nbsp;2024.{" "}
                  <a
                    href={
                      "https://www.abpi.org.uk/publications/code-of-practice-for-the-pharmaceutical-industry-2024/"
                    }
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://www.abpi.org.uk/publications/code-of-practice-for-the-pharmaceutical-industry-2024/.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  U.S.&nbsp;Bureau of&nbsp;Labor Statistics. (2024). Wholesale
                  and&nbsp;Manufacturing Sales Representatives&nbsp;–
                  Occupational Outlook Handbook.{" "}
                  <a
                    href={
                      "https://www.bls.gov/ooh/sales/wholesale-and-manufacturing-sales-representatives.html"
                    }
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://www.bls.gov/ooh/sales/wholesale-and-manufacturing-sales-representatives.htm.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Salas,&nbsp;E., et&nbsp;al. (2012). The&nbsp;science
                  of&nbsp;training and development in&nbsp;organizations.
                  Psychological Science in&nbsp;the&nbsp;Public Interest.{" "}
                  <a
                    href={"https://doi.org/10.1177/1529100612436661"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://doi.org/10.1177/1529100612436661.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Cook,&nbsp;D.A., et&nbsp;al. (2020). Technology-enhanced
                  simulation for&nbsp;health professions education: systematic
                  review and meta-analysis. Advances in&nbsp;Simulation.{" "}
                  <a
                    href={"https://doi.org/10.1186/s41077-020-00138-9"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://doi.org/10.1186/s41077-020-00138-9.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Cheng,&nbsp;A., et&nbsp;al. (2020). Improving the&nbsp;quality
                  of&nbsp;simulation-based research in&nbsp;healthcare.
                  Simulation in&nbsp;Healthcare.{" "}
                  <a
                    href={"https://doi.org/10.1097/SIH.0000000000000416"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://doi.org/10.1097/SIH.0000000000000416.
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Best Practices,&nbsp;LLC. (2014). Pharmaceutical Sales
                  Training Excellence: Tools, Processes &&nbsp;Resources That
                  Drive Effectiveness&nbsp;(PSM-292). Best Practices, LLC.
                  (Proprietary benchmarking report; available
                  from&nbsp;the&nbsp;publisher).{" "}
                  <a
                    href={
                      "https://www.best-in-class.com/bestp/domrep.nsf/products/DECB3DD799C93FCD85257B1A006D96C1!OpenDocument"
                    }
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://www.best-in-class.com/bestp/domrep.nsf/products/DECB3DD799C93FCD85257B1A006D96C1!OpenDocument
                  </a>
                </span>
              </li>

              <li>
                <span>
                  Spurling,&nbsp;G.K.P., et&nbsp;al. (2010). Information
                  from&nbsp;pharmaceutical companies and physicians’
                  prescribing. PLoS Medicine.{" "}
                  <a
                    href={"https://doi.org/10.1371/journal.pmed.1000352"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    https://doi.org/10.1371/journal.pmed.1000352.
                  </a>
                </span>
              </li>
            </ol>
          </RootDesc>
        </div>
      </Paragraph>
    </>
  );
};
